import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Pencil, Trash2, CheckCircle2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { segurosApi } from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { useAuth } from '../../../lib/auth';

const fmtCLP = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString('es-CL')}`);

export function DetallePolizaDialog({ polizaId, onClose, onChanged, onEdit }) {
  const { user } = useAuth();
  const [poliza, setPoliza] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.rol === 'admin' || user?.rol === 'gerencia';

  const load = useCallback(async () => {
    try {
      const res = await segurosApi.detallePoliza(polizaId);
      setPoliza(res.data);
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    }
  }, [polizaId]);

  useEffect(() => { load(); }, [load]);

  const pagar = async (numero) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await segurosApi.pagarCuota(polizaId, numero);
      toast.success(`✓ Cuota ${numero} pagada: ${fmtCLP(res.data.monto_clp)}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const despagar = async (numero) => {
    if (busy) return;
    setBusy(true);
    try {
      await segurosApi.despagarCuota(polizaId, numero);
      toast.success(`Cuota ${numero} revertida a pendiente`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const anular = async () => {
    if (!window.confirm('¿Anular esta póliza? Dejará de aparecer en el calendario.')) return;
    try {
      await segurosApi.anularPoliza(polizaId);
      toast.success('Póliza anulada');
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (!poliza) return null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-detalle-poliza">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center justify-between gap-2 pr-6">
            <span>Póliza {poliza.numero_poliza} · {poliza.aseguradora}</span>
            <span className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit?.(poliza)} data-testid="btn-editar-poliza" title="Editar">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {isAdmin && poliza.estado === 'activa' && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={anular} data-testid="btn-anular-poliza" title="Anular póliza">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {poliza.bien_asegurado}
            {poliza.patente ? ` · Patente ${poliza.patente}` : ''}
            {poliza.corredora ? ` · Corredora: ${poliza.corredora}` : ''}
            {poliza.vigencia ? ` · Vigencia hasta ${fmtFecha(poliza.vigencia)}` : ''}
            {poliza.beneficiario ? ` · Beneficiario: ${poliza.beneficiario}` : ''}
            {poliza.notas ? ` · ${poliza.notas}` : ''}
            {poliza.uf_hoy && <> · UF hoy: ${Number(poliza.uf_hoy.valor).toLocaleString('es-CL', { minimumFractionDigits: 2 })}</>}
          </DialogDescription>
        </DialogHeader>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-1.5">#</th>
              <th className="px-2 py-1.5">Vencimiento</th>
              <th className="px-2 py-1.5 text-right">UF</th>
              <th className="px-2 py-1.5 text-right">Valor UF</th>
              <th className="px-2 py-1.5 text-right">CLP</th>
              <th className="px-2 py-1.5">Estado</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {poliza.cuotas.map((c) => (
              <tr key={c.numero} className="border-b" data-testid={`cuota-row-${c.numero}`}>
                <td className="px-2 py-1.5 text-xs">{c.numero}</td>
                <td className="px-2 py-1.5 text-xs">{fmtFecha(c.fecha_vencimiento)}</td>
                <td className="px-2 py-1.5 text-right">{Number(c.uf).toLocaleString('es-CL', { maximumFractionDigits: 4 })}</td>
                <td className="px-2 py-1.5 text-right text-xs">{c.uf_valor_usado ? `$${Number(c.uf_valor_usado).toLocaleString('es-CL', { minimumFractionDigits: 2 })}` : '—'}</td>
                <td className="px-2 py-1.5 text-right font-medium">
                  {fmtCLP(c.clp)}
                  {c.clp_provisorio && <span className="ml-1 text-[9px] text-amber-600" title="Calculado con UF de hoy; se ajustará al vencimiento">~</span>}
                </td>
                <td className="px-2 py-1.5">
                  <Badge variant={c.estado === 'pagada' ? 'default' : 'outline'} className={`text-[10px] ${c.estado === 'pagada' ? 'bg-emerald-600' : ''}`}>
                    {c.estado === 'pagada' ? `pagada ${fmtFecha(c.fecha_pago)}` : 'pendiente'}
                  </Badge>
                </td>
                <td className="px-2 py-1.5 text-right">
                  {c.estado !== 'pagada' ? (
                    <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" disabled={busy} onClick={() => pagar(c.numero)} data-testid={`btn-pagar-cuota-${c.numero}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Pagar
                    </Button>
                  ) : isAdmin && (
                    <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2 text-muted-foreground" disabled={busy} onClick={() => despagar(c.numero)} data-testid={`btn-despagar-cuota-${c.numero}`}>
                      <Undo2 className="w-3 h-3 mr-1" /> Revertir
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
